/**
 * Slug helpers — derive a tenant slug from a human-readable name.
 *
 * Constraint mirror of the backend regex (`api/app/schemas/tenant.py`):
 *   ^[a-z][a-z0-9_]{1,46}$
 *
 * - lowercase letters/digits/underscore only
 * - must start with a letter
 * - 2–47 chars
 *
 * Because the slug ends up as a Postgres schema name (`tenant_<slug>`),
 * loose input would force us to quote identifiers — strict character
 * set is non-negotiable here. This function does its best to produce
 * a valid slug; when the input is hopeless (empty / starts with a
 * digit / has no letters) it returns "" and the user must type one
 * manually. The form validates the final value either way.
 */

/**
 * Best-effort transform: NFD-normalise to strip accents, replace any
 * non-alphanumeric run with a single underscore, trim leading/trailing
 * underscores, truncate to 47 chars.
 *
 * Returns "" if the result would start with a non-letter or be too
 * short — better to leave the slug field empty than show the user a
 * value that won't pass validation.
 */
export function slugifyName(name: string): string {
  if (!name) return '';

  // NFD decomposes accented chars into base + combining mark. Then
  // `\p{M}` (Unicode "mark" category) strips just the marks, so
  // "São" → "S" + "a" + U+0303 + "o" → "Sao".
  const stripped = name.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

  const cleaned = stripped
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 47);

  // Slug must start with a letter; reject numeric/empty starts so the
  // user gets a blank field they need to fill in themselves rather
  // than a value that'll fail the regex on submit.
  if (cleaned.length < 2 || !/^[a-z]/.test(cleaned)) {
    return '';
  }
  return cleaned;
}
